import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Activity, ShieldAlert, Brain, Zap, ArrowUpRight, ArrowDownRight, Sparkles, Clock, TrendingUp, AlertTriangle, Radio } from "lucide-react";
import { AreaChart, Area, LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, BarChart, Bar } from "recharts";
import api from "@/lib/api";
import { GlassCard, SectionLabel, SeverityBadge, GradientButton, GhostButton } from "@/components/primitives";
import CountUp from "@/components/CountUp";
import useActivityStream from "@/hooks/useActivityStream";
import IncidentHeatmap from "@/components/IncidentHeatmap";

const tooltipStyle = { background: "rgba(15,23,42,0.92)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, padding: 10, fontSize: 12, fontFamily: "IBM Plex Mono" };

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const { connected, items: liveActivity, seed } = useActivityStream();

  useEffect(() => {
    api.get("/analytics/overview").then((r) => setData(r.data)).catch(() => {});
    api.get("/incidents").then((r) => setIncidents(r.data.slice(0, 5))).catch(() => {});
    api.get("/activity").then((r) => seed(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const simulateEvent = async () => { try { await api.post("/activity/simulate"); } catch (_) { /* ignore */ } };

  if (!data) return <div className="text-slate-400">Loading dashboard…</div>;

  const k = data.kpi;
  const kpis = [
    { label: "Incident Health", value: k.incident_health, suffix: "%", icon: Activity, trend: "+2.3%", up: true, grad: "from-emerald-400 to-cyan-400" },
    { label: "System Health", value: k.system_health, suffix: "%", icon: Zap, trend: "+0.4%", up: true, grad: "from-cyan-400 to-blue-500" },
    { label: "AI Confidence", value: k.ai_confidence, suffix: "%", icon: Brain, trend: "+4.1%", up: true, grad: "from-blue-500 to-violet-500" },
    { label: "Risk Score", value: k.risk_score, suffix: "", icon: ShieldAlert, trend: "-6.0%", up: false, grad: "from-violet-500 to-cyan-400" },
    { label: "Response Time", value: k.avg_response_min, suffix: " min", icon: Clock, trend: "-12%", up: false, grad: "from-cyan-400 to-violet-500" },
    { label: "Open Incidents", value: k.open_incidents, icon: AlertTriangle, trend: `${k.open_incidents}`, up: false, grad: "from-orange-400 to-red-500" },
    { label: "Resolved", value: k.resolved_incidents, icon: TrendingUp, trend: "+18%", up: true, grad: "from-emerald-500 to-cyan-400" },
    { label: "AI Assists / day", value: 142, icon: Sparkles, trend: "+34%", up: true, grad: "from-blue-500 to-violet-500" },
  ];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <SectionLabel>Overview · Production</SectionLabel>
          <h1 className="mt-1 text-3xl font-bold tracking-tight">Operations <span className="gradient-text">Command Center</span></h1>
        </div>
        <Link to="/app/copilot"><GradientButton data-testid="dashboard-open-copilot"><Sparkles className="h-4 w-4" /> Ask Copilot</GradientButton></Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {kpis.map((m, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
            <GlassCard className="p-5">
              <div className="flex items-center justify-between">
                <SectionLabel>{m.label}</SectionLabel>
                <m.icon className="h-4 w-4 text-cyan-300" />
              </div>
              <div className="mt-2 font-mono text-3xl font-semibold"><CountUp value={m.value} suffix={m.suffix || ""} /></div>
              <div className={`mt-2 text-xs flex items-center gap-1 ${m.up ? "text-emerald-400" : "text-rose-300"}`}>
                {m.up ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />} {m.trend} <span className="text-slate-500">vs last week</span>
              </div>
              <div className="mt-3 h-8">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.trends.slice(-10).map((t, j) => ({ x: j, v: t.incidents + (i % 3) * 2 }))}>
                    <defs>
                      <linearGradient id={`g${i}`} x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor="#00F0FF" stopOpacity={0.5} />
                        <stop offset="100%" stopColor="#00F0FF" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <Area dataKey="v" stroke="#00F0FF" strokeWidth={1.4} fill={`url(#g${i})`} isAnimationActive />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </GlassCard>
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <GlassCard className="lg:col-span-2 p-6">
          <div className="flex items-center justify-between">
            <div>
              <SectionLabel>Incident Trends</SectionLabel>
              <h3 className="text-lg font-semibold mt-1">Last 14 days</h3>
            </div>
            <div className="flex gap-3 text-xs text-slate-400">
              <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-cyan-400" /> Incidents</span>
              <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-violet-400" /> Resolved</span>
              <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-400" /> AI Assists</span>
            </div>
          </div>
          <div className="h-72 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.trends}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="#64748B" fontSize={11} />
                <YAxis stroke="#64748B" fontSize={11} />
                <Tooltip contentStyle={tooltipStyle} />
                <Line type="monotone" dataKey="incidents" stroke="#00F0FF" strokeWidth={2} dot={{ r: 0 }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="resolved" stroke="#9D4CDD" strokeWidth={2} dot={{ r: 0 }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="ai_assists" stroke="#10B981" strokeWidth={2} dot={{ r: 0 }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>

        <GlassCard className="p-6">
          <div className="flex items-center justify-between">
            <SectionLabel>Activity Feed</SectionLabel>
            <div className="flex items-center gap-2" data-testid="activity-stream-status" data-status={connected ? "live" : "offline"}>
              <span className={`h-1.5 w-1.5 rounded-full ${connected ? "bg-emerald-400 animate-pulse" : "bg-slate-500"}`} />
              <span className="text-[10px] font-mono uppercase tracking-wider text-slate-400">{connected ? "Live" : "Offline"}</span>
              <button onClick={simulateEvent} title="Simulate event" data-testid="activity-simulate" className="ml-1 p-1 rounded hover:bg-white/10 text-slate-400"><Radio className="h-3.5 w-3.5" /></button>
            </div>
          </div>
          <div className="mt-3 space-y-3 max-h-80 overflow-auto pr-1 scrollbar-thin">
            <AnimatePresence initial={false}>
              {liveActivity.map((a) => (
                <motion.div key={a.id} initial={{ opacity: 0, x: -12, height: 0 }} animate={{ opacity: 1, x: 0, height: "auto" }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }} className="flex items-start gap-3">
                  <div className="h-7 w-7 rounded-lg bg-white/[0.04] border border-white/10 grid place-items-center">
                    <Sparkles className="h-3.5 w-3.5 text-cyan-300" />
                  </div>
                  <div className="text-sm">
                    <span className="font-semibold">{a.actor}</span> <span className="text-slate-400">{a.action}</span> <span className="text-cyan-300">{a.target}</span>
                    <div className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">{a.kind}</div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
            {liveActivity.length === 0 && <div className="text-xs text-slate-500">Awaiting events…</div>}
          </div>
        </GlassCard>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <GlassCard className="lg:col-span-2 p-6">
          <div className="flex items-center justify-between">
            <div>
              <SectionLabel>Top Open Incidents</SectionLabel>
              <h3 className="text-lg font-semibold mt-1">Triage queue</h3>
            </div>
            <Link to="/app/incidents" className="text-xs text-cyan-300" data-testid="dashboard-view-all-incidents">View all →</Link>
          </div>
          <div className="mt-4 divide-y divide-white/5">
            {incidents.map((it) => (
              <Link to={`/app/incidents/${it.id}`} key={it.id} className="flex items-center justify-between gap-3 py-3 hover:bg-white/[0.02] rounded-lg px-2 -mx-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{it.title}</div>
                  <div className="text-[11px] text-slate-500 font-mono uppercase tracking-wider mt-0.5">{it.service} · {it.department}</div>
                </div>
                <div className="flex items-center gap-2">
                  <SeverityBadge value={it.severity} />
                  <span className="text-[11px] font-mono text-cyan-300">{Math.round((it.ai_confidence || 0.8) * 100)}%</span>
                </div>
              </Link>
            ))}
          </div>
        </GlassCard>

        <GlassCard className="p-6">
          <SectionLabel>AI Recommendations</SectionLabel>
          <div className="mt-3 space-y-2 text-sm">
            {[
              "Restart auth-service in EU-WEST-1 to clear stuck JWKS cache.",
              "Scale checkout-web horizontally — projected 11% spike at 14:00 UTC.",
              "Rotate API key for partner-webhook-9b3c (last used 92 days ago).",
              "Apply runbook KB-014 to mitigate kafka backpressure on revenue-pipeline."
            ].map((t, i) => (
              <div key={i} className="flex items-start gap-2 p-2 rounded-lg hover:bg-white/[0.04]">
                <Sparkles className="h-3.5 w-3.5 text-cyan-300 mt-0.5" />
                <span className="text-slate-300">{t}</span>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>

      <IncidentHeatmap />
    </motion.div>
  );
}
